import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { CustomAttributesEntity } from 'src/graphql/entities/custom-attributes/custom-attributes-config/custom-attributes-config';
import { useNormalizedCustomAttributeDefinitions } from 'src/graphql/entities/custom-attributes/hooks';
import type { CustomViewEntity } from 'src/graphql/entities/custom-view/custom-view-config/custom-view-config';
import {
  CustomViewAttribute,
  CustomViewFormConditionField,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { useGroupRules } from 'src/graphql/entities/group-rules/group-rules';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { usePlatformCatalogAccess } from 'src/graphql/entities/group-rules/platform-catalog-access';
import { EntityNames } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import {
  buildCustomAttributeColumnRenderers,
  CustomAttributeColumnRenderer,
} from './column-renderers';
import {
  buildCustomAttributeColumnDescriptors,
  buildCustomAttributeFilterDescriptors,
} from './descriptors';

// Grid entities with a custom-attributes catalog (the definition `entity` string differs from
// the grid entity name for sites). Project has no definitions yet, so it surfaces none.
const CUSTOM_VIEW_TO_CUSTOM_ATTRIBUTES_ENTITY: Partial<
  Record<CustomViewEntity, CustomAttributesEntity>
> = {
  [EntityNames.Conversation]: 'Conversation',
  [EntityNames.Site]: 'Address',
  [EntityNames.Client]: 'Client',
};

// Rules of hooks force an unconditional call with a concrete entity. A disabled catalog passes this
// placeholder with an undefined partnerId, which skips the query; the result is discarded below.
const PLACEHOLDER_DEFINITIONS_ENTITY: CustomAttributesEntity = 'Conversation';

export interface CustomViewCustomAttributeCatalog {
  columnDescriptors: CustomViewAttribute[];
  columnDescriptorsById: Map<string, CustomViewAttribute>;
  filterDescriptors: CustomViewFormConditionField[];
  filterDescriptorsById: Map<string, CustomViewFormConditionField>;
  // Per-column read+render bound to the definition; consumed by the grid table (the Phase-4 seam).
  columnRenderersById: Map<string, CustomAttributeColumnRenderer>;
  loading: boolean;
}

const EMPTY_COLUMN_DESCRIPTORS: CustomViewAttribute[] = [];
const EMPTY_FILTER_DESCRIPTORS: CustomViewFormConditionField[] = [];

// Stable singleton for the disabled/dormant path. The definitions hook returns a fresh `[]` whenever
// its query is skipped, so `visibleDefinitions` churns identity every render; returning this constant
// keeps the catalog (and the hot-grid config memo that depends on it) referentially stable. A skipped
// query is never loading, so `loading: false` is correct for every disabled case.
const EMPTY_CATALOG: CustomViewCustomAttributeCatalog = {
  columnDescriptors: EMPTY_COLUMN_DESCRIPTORS,
  columnDescriptorsById: new Map(),
  filterDescriptors: EMPTY_FILTER_DESCRIPTORS,
  filterDescriptorsById: new Map(),
  columnRenderersById: new Map(),
  loading: false,
};

/**
 * Catalog of custom-attribute column + filter descriptors for a grid entity, ready to merge at the
 * `useCustomViewEntityConfig` seam. Memoized for referential stability.
 *
 * dormant: the catalog never changes emitted SQL (the query follows the user's selections). disabled
 * (empty) for unwired entities. in the all-partners view it surfaces the platform partner's shared
 * attributes, see the per-partner vs. platform handling below.
 */
export function useCustomViewCustomAttributes(
  entity: CustomViewEntity,
): CustomViewCustomAttributeCatalog {
  const customAttributesEntity = CUSTOM_VIEW_TO_CUSTOM_ATTRIBUTES_ENTITY[entity] ?? null;
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { groupRules } = useGroupRules();

  const { includePlatformLevelData } = usePlatformCatalogAccess(
    'CustomAttributeDefinition',
    selectedPartnerId,
  );

  const canViewCustomAttributes = Boolean(
    groupRules[UserGroupRules.AccessToViewCustomAttributes],
  );

  const isMultitenancy = isNullish(selectedPartnerId);

  const isEnabled =
    canViewCustomAttributes &&
    customAttributesEntity !== null &&
    (!isMultitenancy || includePlatformLevelData);

  // undefined partnerId skips the underlying query, so a disabled catalog never fetches.
  const partnerId = isEnabled ? (selectedPartnerId ?? PLATFORM_PARTNER_ID) : undefined;

  const { visibleDefinitions, loading } = useNormalizedCustomAttributeDefinitions({
    partnerId,
    entity: customAttributesEntity ?? PLACEHOLDER_DEFINITIONS_ENTITY,
  });

  return useMemo<CustomViewCustomAttributeCatalog>(() => {
    if (!isEnabled) {
      return EMPTY_CATALOG;
    }

    const columnDescriptors = buildCustomAttributeColumnDescriptors(visibleDefinitions);
    const filterDescriptors = buildCustomAttributeFilterDescriptors(
      visibleDefinitions,
      entity,
    );

    return {
      columnDescriptors,
      columnDescriptorsById: new Map(
        columnDescriptors.map((column) => [column.attribute, column]),
      ),
      filterDescriptors,
      filterDescriptorsById: new Map(
        filterDescriptors.map((filter) => [filter.value, filter]),
      ),
      columnRenderersById: buildCustomAttributeColumnRenderers(visibleDefinitions),
      loading,
    };
  }, [isEnabled, visibleDefinitions, entity, loading]);
}
